/**
 * Guest Join Page
 * Allows 3rd party guests to join via invite link
 * Nordic Sanctuary design for non-video states
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useAction } from "convex/react";
import { api } from "@jourfx/convex/client";
import {
  useVideoRoom,
  useMediaControls,
  useReconnection,
  VideoStage,
  ConnectionStatus,
  ReconnectionOverlay,
  TooltipProvider,
  Input,
  Label,
  Button,
} from "@jourfx/ui";
import { UserPlus, AlertCircle } from "lucide-react";
import { ClientLayout } from "../components/layout/ClientLayout";
import { PrivacyNoticeModal } from "../components/consent/PrivacyNoticeModal";
import {
  CallControls,
  DeviceSettingsDialog,
  LeaveConfirmDialog,
  useDeviceSettings,
} from "../components/video/CallControls";

type JoinState = "validating" | "name_entry" | "joining" | "connected" | "error";

export function GuestJoinPage() {
  const { token } = useParams({ from: "/join/$token" });
  const navigate = useNavigate();
  const { t } = useTranslation("video");

  const [joinState, setJoinState] = useState<JoinState>("validating");
  const [guestName, setGuestName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [errorHeading, setErrorHeading] = useState<string | null>(null);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [livekitToken, setLivekitToken] = useState<string | null>(null);
  const [serverUrl, setServerUrl] = useState<string | null>(null);
  const [e2eeSharedKey, setE2eeSharedKey] = useState<string | null>(null);
  const [forceLegacySignalPath, setForceLegacySignalPath] = useState(true);
  const [guestPrivacyAccepted, setGuestPrivacyAccepted] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const hasConnectedRef = useRef(false);
  const hasStartedJoinRef = useRef(false);
  const shouldValidateInvite =
    joinState === "validating" || joinState === "name_entry";

  useEffect(() => {
    if (joinState === "name_entry") {
      nameInputRef.current?.focus();
    }
  }, [joinState]);

  // Validate invite
  const inviteStatus = useQuery(
    api.video.invite.validateInvite,
    token && shouldValidateInvite ? { token } : "skip"
  );

  const generateToken = useAction(api.video.token.generateToken);

  // Check invite validity
  useEffect(() => {
    if (!inviteStatus) return;

    if (inviteStatus.valid) {
      setJoinState("name_entry");
      setShowPrivacyModal(true);
    } else if (!hasStartedJoinRef.current) {
      setError(inviteStatus.error || t("guest.defaultError"));
      setErrorHeading(t("guest.invalidInvite"));
      setJoinState("error");
    }
  }, [inviteStatus, t]);

  // Handle name submit
  const handleJoin = async () => {
    if (!token || !guestName.trim()) return;

    hasStartedJoinRef.current = true;
    setJoinState("joining");
    setError(null);

    try {
      const result = await generateToken({
        inviteToken: token,
        guestName: guestName.trim(),
        guestPrivacyAccepted: true,
      });

      setLivekitToken(result.token);
      setServerUrl(result.url);
      setE2eeSharedKey(result.e2eeKey);
      setForceLegacySignalPath(result.signalPath ? result.signalPath === "v0" : true);
      setSessionStartTime(Date.now());
      setJoinState("connected");
    } catch (err: unknown) {
      const convexCode =
        typeof err === "object" &&
        err !== null &&
        "data" in err &&
        typeof (err as { data?: { code?: string } }).data?.code === "string"
          ? (err as { data: { code: string } }).data.code
          : null;
      const convexMessage =
        typeof err === "object" &&
        err !== null &&
        "data" in err &&
        typeof (err as { data?: { message?: string } }).data?.message === "string"
          ? (err as { data: { message: string } }).data.message
          : null;
      const errorMessage = convexCode === "CONSENT_REQUIRED"
        ? t("guest.privacyRequired")
        : (convexMessage || (err instanceof Error ? err.message : t("guest.joinFailed")));
      setErrorHeading(t("guest.joinFailed"));
      setError(errorMessage);
      setJoinState("error");
    }
  };

  const e2eeOptions = useMemo(
    () => (e2eeSharedKey ? { sharedKey: e2eeSharedKey } : undefined),
    [e2eeSharedKey]
  );

  // Video room hook (for when we have a token)
  const {
    room,
    connectionState,
    connectionQuality,
    localParticipant,
    remoteParticipants,
    spotlightParticipant,
    isE2EEEnabled,
    connect,
    disconnect,
  } = useVideoRoom({
    serverUrl: serverUrl || "",
    token: livekitToken || "",
    role: "guest",
    forceLegacySignalPath,
    e2ee: e2eeOptions,
  });

  useEffect(() => {
    if (
      joinState === "connected" &&
      livekitToken &&
      serverUrl &&
      e2eeSharedKey &&
      !hasConnectedRef.current
    ) {
      hasConnectedRef.current = true;
      connect().catch((err) => {
        console.error("Failed to connect guest:", err);
        setErrorHeading(t("guest.joinFailed"));
        setError(err instanceof Error ? err.message : t("guest.joinFailed"));
        setJoinState("error");
      });
    }
  }, [joinState, livekitToken, serverUrl, e2eeSharedKey, connect, t]);

  useEffect(() => {
    if (joinState !== "connected") {
      hasConnectedRef.current = false;
    }
  }, [joinState]);

  useEffect(() => {
    if (connectionState === "connected" && room) {
      room.startAudio().catch(console.error);
    }
  }, [connectionState, room]);

  // Media controls
  const {
    isMicEnabled,
    isCameraEnabled,
    toggleMic,
    toggleCamera,
  } = useMediaControls({ room });

  // Reconnection
  const {
    isReconnecting,
    attempt,
    maxAttempts,
    canManualRetry,
    manualReconnect,
    lastError,
  } = useReconnection({ room });

  const deviceSettings = useDeviceSettings(room);

  const handleLeave = useCallback(() => {
    setShowLeaveConfirm(true);
  }, []);

  const confirmLeave = useCallback(() => {
    setShowLeaveConfirm(false);
    disconnect();
    navigate({ to: "/" });
  }, [disconnect, navigate]);

  const connectionLabels = useMemo(() => ({
    encryptionActive: t("connection.encryptionActive"),
    notEncrypted: t("connection.notEncrypted"),
    connecting: t("connection.connecting"),
    reconnecting: t("connection.reconnecting"),
    disconnected: t("connection.disconnected"),
    qualityExcellent: t("connection.qualityExcellent"),
    qualityGood: t("connection.qualityGood"),
    qualityPoor: t("connection.qualityPoor"),
    connected: t("connection.connected"),
  }), [t]);

  // Validating state
  if (joinState === "validating") {
    return (
      <ClientLayout>
        <div className="client-card w-full max-w-md p-8 sm:p-10 text-center">
          <div className="client-spinner mx-auto mb-4" aria-hidden="true" />
          <p className="text-client-muted font-medium">
            {t("guest.validating")}
          </p>
        </div>
      </ClientLayout>
    );
  }

  // Error state
  if (joinState === "error") {
    return (
      <ClientLayout>
        <div className="client-card w-full max-w-md p-8 sm:p-10 text-center">
          {/* Icon */}
          <div className="mx-auto w-16 h-16 rounded-full bg-[oklch(0.97_0.02_25)] flex items-center justify-center mb-6">
            <AlertCircle
              className="w-8 h-8 text-[oklch(0.55_0.20_25)]"
              aria-hidden="true"
            />
          </div>

          {/* Content */}
          <h1 className="text-2xl sm:text-3xl font-bold text-client mb-3">
            {errorHeading || t("guest.joinFailed")}
          </h1>
          <p className="text-client-muted text-base sm:text-lg leading-relaxed mb-8">
            {error}
          </p>

          {/* Action */}
          <Button
            variant="secondary"
            size="client"
            onClick={() => navigate({ to: "/" })}
          >
            {t("guest.backToHome")}
          </Button>
        </div>
      </ClientLayout>
    );
  }

  // Name entry state
  if (joinState === "name_entry") {
    return (
      <ClientLayout>
        <PrivacyNoticeModal
          open={showPrivacyModal}
          title={t("guest.privacy.title")}
          body={t("guest.privacy.body")}
          acceptLabel={t("guest.privacy.accept")}
          declineLabel={t("guest.privacy.decline")}
          onAccept={() => {
            setGuestPrivacyAccepted(true);
            setShowPrivacyModal(false);
          }}
          onDecline={() => {
            setShowPrivacyModal(false);
            navigate({ to: "/" });
          }}
        />
        <div className="client-card w-full max-w-md p-8 sm:p-10">
          {/* Icon */}
          <div className="mx-auto w-16 h-16 rounded-full bg-[var(--sage-100)] flex items-center justify-center mb-6">
            <UserPlus
              className="w-8 h-8 text-[var(--sage-600)]"
              aria-hidden="true"
            />
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-client mb-3">
              {t("guest.joinAsGuest")}
            </h1>
            <p className="text-client-muted text-base sm:text-lg leading-relaxed">
              {t("guest.enterName")}
            </p>
            <button
              type="button"
              className="mt-3 text-sm text-[var(--sage-700)] underline underline-offset-2"
              onClick={() => setShowPrivacyModal(true)}
            >
              {t("guest.privacy.review")}
            </button>
          </div>

          {/* Form */}
          <div className="space-y-6">
            <div className="space-y-2">
              <Label
                htmlFor="name"
                className="text-base font-medium text-client"
              >
                {t("guest.yourName")}
              </Label>
              <Input
                id="name"
                ref={nameInputRef}
                placeholder={t("guest.namePlaceholder")}
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleJoin()}
                className="h-14 text-lg px-4 rounded-xl border-2 border-[var(--stone-200)] focus:border-[var(--sage-500)] focus:ring-[var(--sage-400)]"
              />
            </div>

            <Button
              size="client"
              className="w-full"
              onClick={handleJoin}
              disabled={!guestName.trim() || !guestPrivacyAccepted}
            >
              {t("guest.join")}
            </Button>
          </div>
        </div>
      </ClientLayout>
    );
  }

  // Joining state
  if (joinState === "joining") {
    return (
      <ClientLayout>
        <div className="client-card w-full max-w-md p-8 sm:p-10 text-center">
          <div className="client-spinner mx-auto mb-4" aria-hidden="true" />
          <p className="text-client-muted font-medium">
            {t("guest.connecting")}
          </p>
        </div>
      </ClientLayout>
    );
  }

  // Connected state - video room (dark theme for video)
  if (joinState === "connected" && (!livekitToken || !serverUrl || !e2eeSharedKey)) {
    return (
      <ClientLayout>
        <div className="client-card w-full max-w-md p-8 sm:p-10 text-center">
          <div className="client-spinner mx-auto mb-4" aria-hidden="true" />
          <p className="text-client-muted font-medium">
            {t("guest.connecting")}
          </p>
        </div>
      </ClientLayout>
    );
  }

  return (
    <TooltipProvider>
      <div className="h-screen flex flex-col bg-stone-950">
        <header className="flex items-center justify-center p-4 bg-card/80 backdrop-blur border-b">
          <ConnectionStatus
            status={connectionState}
            connectionQuality={connectionQuality}
            startTime={sessionStartTime || undefined}
            isE2EEEnabled={isE2EEEnabled}
            labels={connectionLabels}
          />
        </header>

        <VideoStage
          localParticipant={localParticipant}
          remoteParticipants={remoteParticipants}
          spotlightParticipant={spotlightParticipant}
          className="flex-1"
        />

        <CallControls
          isMicEnabled={isMicEnabled}
          isCameraEnabled={isCameraEnabled}
          onToggleMic={toggleMic}
          onToggleCamera={toggleCamera}
          onOpenSettings={() => setShowSettings(true)}
          onLeave={handleLeave}
        />

        <ReconnectionOverlay
          isReconnecting={isReconnecting}
          attempt={attempt}
          maxAttempts={maxAttempts}
          canManualRetry={canManualRetry}
          lastError={lastError}
          onManualRetry={manualReconnect}
        />

        <DeviceSettingsDialog
          open={showSettings}
          deviceSettings={deviceSettings}
          onClose={() => setShowSettings(false)}
        />

        <LeaveConfirmDialog
          open={showLeaveConfirm}
          onCancel={() => setShowLeaveConfirm(false)}
          onConfirm={confirmLeave}
        />
      </div>
    </TooltipProvider>
  );
}
